import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAdminEdit } from "@/contexts/AdminEditContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/security";

interface EditableTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  k: string; // translation key
  as?: keyof JSX.IntrinsicElements; // element type
  multiline?: boolean;
  className?: string;
  placeholder?: string;
}

// A lightweight inline editor for translation strings
const EditableText: React.FC<EditableTextProps> = ({
  k,
  as = "span",
  multiline = false,
  className,
  placeholder,
  ...rest
}) => {
  const { editMode } = useAdminEdit();
  const { language, t, updateTranslation } = useLanguage();
  const [value, setValue] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const initial = t(k);
  const Tag: any = as;
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  async function save(newVal: string) {
    if (!editMode) return;
    setSaving(true);
    try {
      // Sanitize input to prevent XSS attacks
      const sanitizedValue = sanitizeHtml(newVal);
      await adminApi({
        model: "translations",
        action: "upsert",
        data: { key: k, language, value: sanitizedValue }
      });
      updateTranslation(k, sanitizedValue);
    } catch (error: any) {
      console.error("Failed to save translation:", error);
      
      // Check for authentication errors in various forms
      const isAuthError = error.message === "Not authenticated" || 
                         error.message?.includes("Invalid or expired session") ||
                         error.message?.includes("Edge Function returned a non-2xx status code") ||
                         (error.context && error.context.status === 401);
      
      if (isAuthError) {
        // Session expired, clear token and redirect to login
        localStorage.removeItem("admin_token");
        console.log("Session expired, redirecting to login");
        window.location.href = "/portal-commodit-7842/login";
        return;
      }
      
      setValue(initial); // Reset to original value on error
    } finally {
      setSaving(false);
    }
  }

  if (!editMode) {
    return (
      <Tag className={className} {...rest}>
        {initial}
      </Tag>
    );
  }

  // Editing UI
  return (
    <span className={cn("relative inline-flex", className)} {...rest}>
      {multiline ? (
        <textarea
          ref={(el) => (inputRef.current = el)}
          className="min-h-[2.5rem] w-full resize-y rounded-md bg-background/70 border border-primary/30 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
          value={value}
          placeholder={placeholder || k}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => save(value)}
        />
      ) : (
        <input
          ref={(el) => (inputRef.current = el)}
          className="rounded-md bg-background/70 border border-primary/30 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
          value={value}
          placeholder={placeholder || k}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => save(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
        />
      )}
      {saving && (
        <span className="ml-2 text-xs text-muted-foreground">Saving…</span>
      )}
    </span>
  );
};

export default EditableText;
